import type { Revalidator, RevalidatorOptions } from 'swr';

import { ApiError } from '#/errors/serviceErrors';

/**
 * Method used for throttling calls to a desired function, with capability to cancel before throttle time is met.
 * @param fn Original function that's intended to be throttled.
 * @param delay Amount the function should be delayed.
 * @returns Newly throttled function, and cancel function for cleanup of throttled function. E.g. const [throttled, cancel] = throttle(() => {}, 200);
 */
export const throttle = <R, A extends any[]>(
  fn: (...args: A) => R,
  delay: number
): [(...args: A) => R | undefined, () => void] => {
  let wait = false;
  let timeout: undefined | number;
  let cancelled = false;

  return [
    (...args: A) => {
      if (cancelled || wait) {
        return undefined;
      }

      const val = fn(...args);

      wait = true;

      timeout = window.setTimeout(() => {
        wait = false;
      }, delay);

      return val;
    },
    () => {
      cancelled = true;
      clearTimeout(timeout);
    },
  ];
};

/**
 * Recommended method of retrying SWR related calls that should revalidate on error.  This method targets 401 status codes, with a detail.status of 'Unauthorized'.
 * Source: https://swr.vercel.app/docs/error-handling#error-retry
 * @param callback Callback method for when the revalidation exits due to retryAmount being met, or incorrect error response.
 * @param retryAmount Amount of times to attempt retry before stopping.
 * @returns SWR onErrorRetry function.
 */
export const makeSwrUnauthorizedRetry = (
  callback: (finished: boolean) => void,
  retryAmount: number = 2
) => {
  return (
    error: ApiError,
    _key: string,
    _config: unknown,
    revalidate: Revalidator,
    { retryCount }: Required<RevalidatorOptions>
  ) => {
    if (
      (error?.statusCode !== 401 &&
        error?.details?.status?.toLowerCase() !== 'unauthorized') ||
      retryCount >= retryAmount
    ) {
      callback(true);

      return;
    }

    revalidate({ retryCount });
  };
};

/**
 * Decodes a base64 string into a UTF-8 format.
 * @param base64 Base64 encoded string that we want to decode to UTF-8.
 * @returns A decoded UTF-8 string.
 *
 * Beware that the atob method can throw an exception if the input string is not correctly encoded in base64.
 * So the method should always be handled within a try-catch block.
 */
export const decodeBase64ToUtf8 = (base64: string) => {
  // Convert the base64 string to a binary string
  const binaryString = atob(base64);

  // Ignoring sonar rule against charCodeAt because converting a binary string into raw bytes, which must be done using UTF-16 code units — not unicode code points (codePointAt)
  const toCode = (char: string) => char.charCodeAt(0); // NOSONAR

  // Convert binary string to an array of bytes.
  const bytes = Uint8Array.from(binaryString, toCode);

  // Use TextDecoder to decode the bytes as UTF-8
  return new TextDecoder('utf-8').decode(bytes);
};

/**
 * Encodes a UTF-8 string into a base64 format.
 * @param text Text that we want to encode to base64.
 * @returns An encoded base64 string.
 */
export const encodeUtf8ToBase64 = (text: string) => {
  // Encode text to UTF-8 bytes
  const bytes = new TextEncoder().encode(text);

  // Convert bytes to binary string
  // Ignoring sonar rule against fromCharCode because we are converting raw bytes into a binary string, which must be done using UTF-16 code units — not unicode code points (fromCodePoint)
  const binaryString = String.fromCharCode(...bytes); // NOSONAR

  // Convert binary string to base64
  return btoa(binaryString);
};
